"use client";


import { useNavigate } from "react-router-dom";
import profoundLogo from "../images/profound-logo.svg";
import profoundWord from "../images/profound-word.svg";
import { useEffect, useState } from "react";
import { useTokenStorage } from "../hooks/useTokenStorage";
import { useWhoAmI } from "../hooks/useWhoAmI";
import { useGetWhoAmIQuery } from "../lib/api/apiSlice";

interface OnboardingPageContentProps {
	linkedinUserData: any | null;
}

export default function OnboardingPageContent({ linkedinUserData: initialLinkedinUserData }: OnboardingPageContentProps) {
	const navigate = useNavigate();
	const [linkedinUserData, setLinkedinUserData] = useState(initialLinkedinUserData);
	const [linkedinLoading, setLinkedinLoading] = useState(!initialLinkedinUserData);
	const [isCheckingProfile, setIsCheckingProfile] = useState(false);

	// Handle token storage from URL
	useTokenStorage();
	
	// Get whoami hook to access loading state
	const { loading: whoamiLoading } = useWhoAmI();
	
	// Get referral code for whoami query
	const referralCode = typeof window !== "undefined" ? localStorage.getItem("referral_code") : null;
	const { refetch: refetchWhoAmI } = useGetWhoAmIQuery(
		{ referralCode: referralCode || undefined },
		{ skip: false }
	);

	const handleCheckProfile = async () => {
		setIsCheckingProfile(true);
		
		try {
			// First, complete the onboarding
			await fetchUserProfile();
			
			// Get referral code if available
			const referralCode = localStorage.getItem("referral_code");
			const whoamiUrl = referralCode 
				? `/protected/whoami?referral_code=${encodeURIComponent(referralCode)}`
				: "/protected/whoami";
			
			// Make whoami API call to get updated account status
			const { fetcher } = await import("../lib/fetcher");
			const whoamiData = await fetcher<{ success: boolean; data: any }>(whoamiUrl, {
				method: "GET",
			});
			
			// Update sessionStorage with new whoami data
			if (whoamiData?.success && whoamiData.data) {
				sessionStorage.setItem("whoami_user_data", JSON.stringify(whoamiData.data));
				// Dispatch event to notify redirect components that whoami data has been updated
				if (typeof window !== "undefined") {
					window.dispatchEvent(new Event("whoamiUpdated"));
				}
			}
			
			// Also trigger RTK Query refetch to update the cache
			await refetchWhoAmI();
			
			// Wait a bit for the redirect logic to process the updated data
			await new Promise(resolve => setTimeout(resolve, 300));
			
			// Let the redirect logic handle the redirect based on updated status
			// The AccountStatusRedirect component will check the updated whoami data
			// and redirect to the appropriate page
			navigate("/profile");
		} catch (error) {
			console.error("Error checking profile:", error);
			// Still redirect to profile even if there's an error
			navigate("/profile");
		} finally {
			setIsCheckingProfile(false);
		}
	};

	// Fetch LinkedIn profile if not available from server
	useEffect(() => {
		const fetchLinkedInProfile = async () => {
			// If we already have data from server, don't fetch again
			if (initialLinkedinUserData) {
				setLinkedinLoading(false);
				return;
			}

			// Check if we have a token
			const token = localStorage.getItem("accessToken");
			if (!token) {
				setLinkedinLoading(false);
				return;
			}

			setLinkedinLoading(true);

			try {
				const { fetcher } = await import("../lib/fetcher");
				const data = await fetcher<{ success: boolean; data: any }>(
					`/protected/user-profile?detailed=true`,
					{
						method: "GET",
					},
				);

				if (data?.success && data.data) {
					setLinkedinUserData(data.data);
					setLinkedinLoading(false);
				} else {
					setLinkedinLoading(false);
				}
			} catch (error) {
				console.error("Error fetching LinkedIn profile:", error);
				setLinkedinLoading(false);
			}
		};

		fetchLinkedInProfile();
	}, [initialLinkedinUserData]); // Run when initialLinkedinUserData changes

	// Check if we have access token from localStorage
	useEffect(() => {
		const checkAuth = () => {
			const token = localStorage.getItem("accessToken");
			if (token) {
			}
		};
		checkAuth();

		// Listen for token storage events
		const handleTokenStored = () => {
			checkAuth();
		};
		window.addEventListener("tokenStored", handleTokenStored);
		return () => {
			window.removeEventListener("tokenStored", handleTokenStored);
		};
	}, []);

	const fetchUserProfile = async () => {
		try {
			const { fetcher } = await import("../lib/fetcher");
			await fetcher(
				`/protected/user-profile/complete-onboarding`,
				{
					method: "POST",
				},
			);
		} catch (error) {
			console.error("Error fetching onboarding data:", error);
		}
	};

	return (
		<div
			className="min-h-screen flex flex-col items-center justify-start pt-4 sm:pt-6 md:pt-8 px-4 sm:px-6 md:px-8 pb-8"
			style={{
				background:
					"linear-gradient(180deg, #F8F4C5 0%, #E4C5FF 49.52%, #F8F4C5 100%)",
			}}
		>
			{/* Loading State */}
			{linkedinLoading ? (
				<div className="flex-1 flex flex-col items-center justify-center w-full">
					<div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-[#10333B] rounded-full flex items-center justify-center mb-8 sm:mb-12">
						<div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 relative">
							<img
								src={profoundLogo}
								alt="Profound Logo"
								className="object-contain p-2 w-full h-full"
								style={{ filter: "brightness(0) invert(1)" }}
							/>
						</div>
					</div>
					<div className="flex items-center justify-center gap-2">
						<svg
							className="animate-spin w-6 h-6 sm:w-8 sm:h-8"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
								style={{ color: "#10333B" }}
							></circle>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								style={{ color: "#10333B" }}
							></path>
						</svg>
						<span
							className="text-lg sm:text-xl md:text-2xl"
							style={{
								fontFamily: "var(--font-inter), Inter, sans-serif",
								color: "#10333B",
							}}
						>
							Loading...
						</span>
					</div>
				</div>
			) : (
				<>
					<div className="w-full flex justify-center mb-8 sm:mb-12 md:mb-16">
						<div className="flex items-center gap-1">
							<div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center relative">
								<div className="absolute inset-0 flex items-center justify-center">
									<div className="w-5 h-5 sm:w-6 sm:h-6 relative">
										<img
											src={profoundLogo}
											alt="Profound Logo"
											className="object-contain w-full h-full"
										/>
									</div>
								</div>
							</div>
							<div className="h-5 sm:h-6 w-auto">
								<img
									src={profoundWord}
									alt="Profound"
									width={105}
									height={22}
									className="h-full w-auto"
								/>
							</div>
						</div>
					</div>

					<div className="flex flex-col items-center text-center max-w-2xl w-full flex-1 justify-center">
						<div className="inline-block mb-3 sm:mb-4">
							<span
								style={{
									fontFamily:
										"var(--font-editorial), 'Playfair Display', serif",
									fontWeight: 400,
									color: "#1D1D1D",
								}}
								className="text-4xl sm:text-5xl md:text-6xl"
							>
								Hey{" "}
							</span>
							<span
								className="text-4xl sm:text-5xl md:text-6xl"
								style={{
									fontFamily:
										"var(--font-editorial), 'Playfair Display', serif",
									fontStyle: "italic",
									fontWeight: 400,
									color: "#1D1D1D",
								}}
							>
								{linkedinUserData?.user?.full_name}!
							</span>
						</div>

						<p
							className="text-lg sm:text-4xl md:text-5xl mb-6 sm:mb-8"
							style={{
								fontFamily: "var(--font-inter), Inter, sans-serif",
								fontWeight: 400,
								color: "#1D1D1D",
							}}
						>
							Welcome back
						</p>

						<h2
							className="text-3xl sm:text-4xl md:text-5xl mb-4 sm:mb-6"
							style={{
								fontFamily: "var(--font-editorial), 'Playfair Display', serif",
								fontWeight: 400,
								color: "#10333B",
							}}
						>
							Your{" "}
							<span
								style={{
									fontFamily:
										"var(--font-editorial), 'Playfair Display', serif",
									fontStyle: "italic",
								}}
							>
								Profile
							</span>{" "}
							is Ready!
						</h2>

						<p
							className="text-base sm:text-lg md:text-xl mb-8 sm:mb-12 text-gray-700"
							style={{
								fontFamily: "var(--font-inter), Inter, sans-serif",
								fontWeight: 400,
								color: "#1D1D1D",
							}}
						>
							Here is what{" "}
							<span
								style={{
									fontFamily:
										"var(--font-editorial), 'Playfair Display', serif",
									fontStyle: "italic",
								}}
							>
								Profound Career Concierge
							</span>{" "}
							has discovered about you
						</p>

						<button
							onClick={handleCheckProfile}
							disabled={isCheckingProfile || whoamiLoading}
							className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-white font-semibold text-sm sm:text-base uppercase tracking-wide transition-opacity hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
							style={{
								backgroundColor: "#10333B",
								fontFamily: "var(--font-inter), Inter, sans-serif",
							}}
						>
							{isCheckingProfile || whoamiLoading ? (
								<>
									<svg
										className="animate-spin w-5 h-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									<span>CHECKING...</span>
								</>
							) : (
								<>
									<span className="material-icons text-white text-xl sm:text-2xl">
										article
									</span>
									<span>CHECK PROFILE SUMMARY</span>
								</>
							)}
						</button>
					</div>
				</>
			)}
		</div>
	);
}

